/**
 * Survives a deploy that happens mid-session.
 *
 * A built SPA is an index.html naming content-hashed chunks. Deploying replaces
 * every chunk and `rsync --delete` removes the old ones — so a browser holding
 * the previous index.html asks for files that no longer exist, and the first
 * lazy-loaded route it tries throws "Failed to fetch dynamically imported
 * module". The navigation fails and the user is stranded on whatever page they
 * were on, with no indication why.
 *
 * The correct fix is cache headers: never cache index.html, cache /assets/
 * forever. This is the belt to that pair of braces, because a client can still
 * be holding an old page from BEFORE those headers were applied, and there is no
 * way to reach out and tell it.
 *
 * Reloading is safe here: a chunk that 404s cannot be retried, and the reload
 * fetches a fresh index.html naming chunks that exist.
 */
const FLAG = "chunk-reload-attempted";

export function installChunkRecovery(router) {
  router.onError((error, to) => {
    const message = String(error?.message ?? "");

    const isMissingChunk =
      message.includes("Failed to fetch dynamically imported module") ||
      message.includes("Importing a module script failed") ||
      message.includes("error loading dynamically imported module");

    if (!isMissingChunk) return;

    // Once only. If a fresh load still cannot find the chunk the problem is not
    // staleness — it is a broken deploy, and an endless reload loop would hide
    // that behind a flickering page.
    if (sessionStorage.getItem(FLAG)) {
      console.error("Chunk still missing after reload; the deployment looks incomplete.", error);
      return;
    }

    sessionStorage.setItem(FLAG, "1");

    // Hard navigation, not router.push — the whole point is to re-fetch
    // index.html rather than route within the stale bundle.
    window.location.assign(to?.fullPath ?? window.location.pathname);
  });

  // Cleared once a navigation completes, so a later deploy gets its own attempt.
  router.afterEach(() => sessionStorage.removeItem(FLAG));
}
